<template>
    <div v-if="ready">
      <slot name="before-table"></slot> <!-- Optional slot for search inputs or other elements before the table -->

      <div class="table-responsive">
        <el-base-table
          :table-data="data"
          :table-columns="columns"
          :allow-click="allowClick"
          :enable-edit="enableEdit"
          :enable-delete="enableDelete"
          :enable-custom-action="enableCustomAction"
          :custom-action="customAction"
          :custom-action-icon="customActionIcon"
          :should-show-custom-action="shouldShowCustomAction"
          :enable-selection="enableSelection"
          :should-show-selection="shouldShowSelection"
          :selected-ids="selectedIds"
          :actions-inline="actionsInline"
          :show-red-lines="showRedLines"
          :red-line-property="redLineProperty"
          @row-click="handleRowClick"
          @edit="handleEdit"
          @delete="handleDelete"
          @selection-change="handleSelectionChange"
        >
          <!-- Forward optional `cell-<prop>` slots to the table -->
          <template v-for="name in Object.keys($slots).filter(n => n.startsWith('cell-'))" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </el-base-table>
      </div>

      <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap pagination">
        <div class="">
          <p class="card-category">
            {{ $t('pagination.showing') }} {{ from + 1 }} {{ $t('pagination.to') }} {{ to }} {{ $t('pagination.of') }} {{ total }} {{ $t('pagination.entries') }}
          </p>
        </div>
        <material-pagination color="info" class="pagination-no-border">
          <material-pagination-item
            prev
            :disabled="currentPage === 1"
            @click="prevPage"
          />
          <material-pagination-item
            v-for="page in displayedPages"
            :key="page"
            :label="String(page)"
            :active="currentPage === page"
            @click="changePage(page)"
          />
          <material-pagination-item
            next
            :disabled="currentPage === totalPages"
            @click="nextPage"
          />
        </material-pagination>
      </div>
    </div>
  </template>

  <script>
  import ElBaseTable from "./ElBaseTable.vue";
  import MaterialPagination from "./MaterialPagination.vue";
  import MaterialPaginationItem from "./MaterialPaginationItem.vue";

  export default {
    components: {
      ElBaseTable,
      MaterialPagination,
      MaterialPaginationItem,
    },
    props: {
      columns: {
        type: Array,
        required: true,
      },
      data: {
        type: Array,
        required: true,
      },
      pagination: {
        type: Object,
        required: true, // { perPage: Number, currentPage: Number, total: Number }
      },
      ready: {
        type: Boolean,
        default: false,
      },
      allowClick: {
        type: Boolean,
        default: true,
      },
      enableEdit: {
        type: Boolean,
        default: false,
      },
      enableDelete: {
        type: Boolean,
        default: false,
      },
      enableCustomAction: {
        type: Boolean,
        default: false,
      },
      customAction: {
        type: Function,
        default: null,
      },
      customActionIcon: {
        type: String,
        default: null,
      },
      shouldShowCustomAction: {
        type: Function,
        default: null,
      },
      enableSelection: {
        type: Boolean,
        default: false,
      },
      shouldShowSelection: {
        type: Function,
        default: null,
      },
      selectedIds: {
        type: Array,
        default: () => [],
      },
      actionsInline: {
        type: Boolean,
        default: false,
      },
      showRedLines: {
        type: Boolean,
        default: false
      },
      redLineProperty: {
        type: String,
        default: ''
      },
      pagesToDisplay: {
        type: Number,
        default: 5
      }
    },
    emits: ['update-page', 'row-click', 'edit', 'delete', 'selection-change'],
    data() {
      return {
        currentPage: this.pagination.currentPage || 1,
      };
    },
    computed: {
      to() {
        let highBound = this.from + this.pagination.perPage;
        return this.total < highBound ? this.total : highBound;
      },
      from() {
        return this.pagination.perPage * (this.currentPage - 1);
      },
      total() {
        return this.pagination.total || 0;
      },
      totalPages() {
        if (this.total > 0) {
          return Math.ceil(this.total / this.pagination.perPage);
        }
        return 1;
      },
      displayedPages() {
        const pages = [];
        const maxPages = Math.min(this.pagesToDisplay, this.totalPages);

        let startPage = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
        let endPage = startPage + maxPages - 1;

        if (endPage > this.totalPages) {
          endPage = this.totalPages;
          startPage = Math.max(1, endPage - maxPages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }
        return pages;
      }
    },
    watch: {
      pagination: {
        handler(newVal) {
          this.currentPage = newVal.currentPage || 1;
        },
        deep: true,
      },
    },
    methods: {
      changePage(page) {
        if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
          this.currentPage = page;
          this.$emit('update-page', page);
        }
      },
      nextPage() {
        if (this.currentPage < this.totalPages) {
          this.changePage(this.currentPage + 1);
        }
      },
      prevPage() {
        if (this.currentPage > 1) {
          this.changePage(this.currentPage - 1);
        }
      },
      handleRowClick(row) {
        this.$emit('row-click', row);
      },
      handleEdit(index, row) {
        this.$emit('edit', index, row);
      },
      handleDelete(index, row) {
        this.$emit('delete', index, row);
      },
      handleSelectionChange(payload) {
        this.$emit('selection-change', payload);
      },
    },
  };
  </script>

  <style scoped>
  /* Add any scoped styles if needed, e.g., for the pagination or table */
  .pagination {
    margin-top: 10px;
  }
  </style>
